import { useEffect, useRef } from 'react';

export const DEFAULT_INACTIVITY = 2 * 60 * 60 * 1000; // 2 hours
export const LAST_ACTIVITY_KEY = 'LAST_ACTIVITY_TS';
export const IDLE_LOGOUT_FLAG_KEY = 'IDLE_LOGOUT_FLAG';
export const AUTH_TOKEN_UPDATED_EVENT = 'auth-token-updated';
export const IDLE_LOGOUT_MESSAGE_TTL = 2 * 60 * 1000;
const ACTIVITY_UPDATE_DEBOUNCE = 1000;

export const getConfiguredInactivity = () => DEFAULT_INACTIVITY;
export const getIdleLogoutFlag = () => {
  const rawValue = localStorage.getItem(IDLE_LOGOUT_FLAG_KEY);
  if (!rawValue) return null;
  const timestamp = Number(rawValue);
  if (!timestamp || Date.now() - timestamp > IDLE_LOGOUT_MESSAGE_TTL) {
    localStorage.removeItem(IDLE_LOGOUT_FLAG_KEY);
    return null;
  }
  return timestamp;
};

const useIdleLogout = (timeout = DEFAULT_INACTIVITY) => {
  const timerId = useRef(null);
  const lastTrackedAtRef = useRef(0);

  useEffect(() => {
    const getLastActivity = () => Number(localStorage.getItem(LAST_ACTIVITY_KEY) || Date.now());

    const clearTimer = () => {
      if (timerId.current) {
        window.clearTimeout(timerId.current);
        timerId.current = null;
      }
    };

    const setLastActivity = (timestamp = Date.now()) => {
      lastTrackedAtRef.current = timestamp;
      localStorage.setItem(LAST_ACTIVITY_KEY, String(timestamp));
    };

    const isAuthenticated = () => Boolean(localStorage.getItem('JWT'));

    const redirectToLogin = () => {
      if (window.location.pathname !== '/login') {
        window.location.replace('/login');
      }
    };

    const logout = () => {
      if (!isAuthenticated()) return;
      localStorage.removeItem('JWT');
      localStorage.removeItem('JWT_EXPIRY');
      localStorage.removeItem(LAST_ACTIVITY_KEY);
      localStorage.setItem(IDLE_LOGOUT_FLAG_KEY, String(Date.now()));
      clearTimer();
      redirectToLogin();
    };

    const hasExceededIdleTimeout = () => {
      if (!isAuthenticated()) return false;
      return Date.now() - getLastActivity() >= timeout;
    };

    const scheduleLogoutCheck = () => {
      clearTimer();
      if (!isAuthenticated()) return;
      const remainingTime = Math.max(timeout - (Date.now() - getLastActivity()), 0);
      timerId.current = window.setTimeout(() => {
        if (hasExceededIdleTimeout()) {
          logout();
        } else {
          scheduleLogoutCheck();
        }
      }, remainingTime);
    };

    const recordActivity = () => {
      if (!isAuthenticated()) return;
      const now = Date.now();
      if (now - lastTrackedAtRef.current < ACTIVITY_UPDATE_DEBOUNCE) return;
      setLastActivity(now);
      scheduleLogoutCheck();
    };

    const validateSession = () => {
      if (!isAuthenticated()) {
        clearTimer();
        return;
      }
      if (hasExceededIdleTimeout()) {
        logout();
        return;
      }
      scheduleLogoutCheck();
    };

    const initializeSessionTimer = (forceReset = false) => {
      if (!isAuthenticated()) {
        clearTimer();
        return;
      }
      if (forceReset || !localStorage.getItem(LAST_ACTIVITY_KEY)) {
        setLastActivity();
      } else {
        lastTrackedAtRef.current = getLastActivity();
      }
      scheduleLogoutCheck();
    };

    const handleStorageChange = (event) => {
      if (event.key === 'JWT') {
        if (event.newValue) {
          initializeSessionTimer(true);
        } else {
          clearTimer();
          redirectToLogin();
        }
      }
      if (event.key === LAST_ACTIVITY_KEY && isAuthenticated()) {
        lastTrackedAtRef.current = Number(event.newValue || Date.now());
        scheduleLogoutCheck();
      }
      if (event.key === IDLE_LOGOUT_FLAG_KEY && getIdleLogoutFlag()) {
        clearTimer();
        redirectToLogin();
      }
    };

    const handleVisibilityOrFocus = () => {
      validateSession();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        validateSession();
      }
    };

    const handleLogin = () => {
      initializeSessionTimer(true);
    };

    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'mousemove', 'wheel', 'touchmove'];
    activityEvents.forEach((event) => window.addEventListener(event, recordActivity, { passive: true }));
    window.addEventListener('focus', handleVisibilityOrFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener(AUTH_TOKEN_UPDATED_EVENT, handleLogin);

    initializeSessionTimer(true);
    validateSession();

    return () => {
      clearTimer();
      activityEvents.forEach((event) => window.removeEventListener(event, recordActivity));
      window.removeEventListener('focus', handleVisibilityOrFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener(AUTH_TOKEN_UPDATED_EVENT, handleLogin);
    };
  }, [timeout]);
};

export default useIdleLogout;
